import React, { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { BorderWrapper, FlexRow } from 'components/styled-components';
import { H3, Button, Intent, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import Pagination from 'components/pagination';
import styled from 'styled-components';
import colors from 'utils/colors';
import { download, fileTypeFilter } from 'utils/functions';
import map from 'lodash/map';
import { useDropzone } from 'react-dropzone';
import { uploadFile, fetchSingleFile, deleteFile } from 'api/utils';
import { AppToaster } from 'components/AppToaster';
import forEach from 'lodash/forEach';
import { fetchUploadedFiles } from 'actions/utils';
import hasPermission from 'utils/hasPermission';
import permissions from 'utils/permissions';

const DocumentRow = ({ document }) => (
  <DocumentWrapper>
    <div style={{ width: '100%' }}>
      <DocumentInfo>
        <Icon icon={IconNames.DOCUMENT} color={colors.lightGray} />
        <Button
          minimal
          large
          text={document.displayName}
          intent={Intent.PRIMARY}
          onClick={() =>
            fetchSingleFile(document.uploadID, 'REVIEW').then(res => {
              download(res, document.displayName);
            })
          }
        />
      </DocumentInfo>
    </div>
  </DocumentWrapper>
);

const DocumentBase = props => {
  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles = acceptedFiles.filter(file => fileTypeFilter(file));
    if (acceptedFiles.length === 0) {
      AppToaster.show({
        message:
          'The files could not be uploaded due to their file type. Try uploading a PDF, MSG, Excel / Word / PowerPoint, or html file',
        intent: Intent.DANGER,
      });
      return;
    }
    forEach(acceptedFiles, file => {
      uploadFile('REVIEW', props.vendorRequestID, file)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          props.fetchUploadedFiles(props.vendorRequestID);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    });
  }, []);

  const PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(0);
  const [docsPage, setDocsPage] = useState([]);

  useEffect(() => {
    props.fetchUploadedFiles(props.vendorRequestID);
  }, []);

  useEffect(() => {
    setDocsPage(getDocsPage);
  }, [props.files, currentPage]);

  const getDocsPage = () => {
    return props.files.slice(
      currentPage * PER_PAGE,
      (currentPage + 1) * PER_PAGE
    );
  };

  const handlePageClick = page => {
    setCurrentPage(page);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <BorderWrapper margin="1rem" padding="1rem">
      <FlexRow>
        <H3>Documents</H3>
        {hasPermission(props.permissions, [
          permissions.UPLOAD_REQUEST_ATTACHMENT,
          permissions.UPLOAD_ALL_REQUEST_ATTACHMENT,
        ]) && (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              minimal
              large
              intent={Intent.PRIMARY}
              icon={IconNames.PLUS}
              text="ADD DOCUMENT"
              style={{ fontWeight: 700 }}
            />
          </div>
        )}
      </FlexRow>

      {map(docsPage, (document, i) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DocumentRow document={document} key={i} />
          <Button
            icon={IconNames.TRASH}
            color={colors.lightGray}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              deleteFile(document.uploadID, 'REVIEW')
                .then(res => {
                  try {
                    AppToaster.show({
                      message: res.data.message,
                      intent: Intent.SUCCESS,
                    });
                    props.fetchUploadedFiles(props.vendorRequestID);
                  } catch (err) {
                    console.err(err);
                  }
                })
                .catch(err => {
                  AppToaster.show({
                    message: err.response.data.message,
                    intent: Intent.DANGER,
                  });
                })
            }
            minimal
          />
        </div>
      ))}
      {props.files.length > PER_PAGE && (
        <Pagination
          numRecords={props.files.length}
          perPage={PER_PAGE}
          currentPage={currentPage}
          onPageClick={handlePageClick}
          handlePerPageChange={null}
          hidePerPage={true}
        />
      )}
    </BorderWrapper>
  );
};

const DocumentWrapper = styled.div`
  display: flex;
  padding: 0.5rem;
  margin: 0.5rem;
`;

const DocumentInfo = styled.div`
  width: 90%;
  display: flex;
  align-content: center;
  align-items: center;
`;

const mapStateToProps = state => ({
  files: state.utils.reviewFiles,
});

const Documents = connect(
  mapStateToProps,
  { fetchUploadedFiles }
)(DocumentBase);

export default Documents;
